import React, { useState, useEffect } from 'react';
import { Users, UserPlus, UserMinus, RefreshCw, Check, X, Filter, Heart, AlertTriangle, Star } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';

interface User {
  id: string;
  display_name: string;
  bio: string;
  preferences: any;
  trust_score_data: any;
  compatibility_score?: number;
  interests?: any[];
  dealbreakers?: any[];
  vibe_tags?: string[];
  created_at: string;
}

export function ManualMatching() {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [potentialMatches, setPotentialMatches] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [matchLoading, setMatchLoading] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<User | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data: users, error } = await supabase
        .from('profiles')
        .select(`
          id,
          display_name,
          bio,
          trust_score_data,
          vibe_tags,
          created_at,
          user_preferences (preferences)
        `)
        .eq('is_active', true)
        .eq('moderation_status', 'approved');

      if (error) throw error;
      setUsers(users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const findPotentialMatches = async (user: User) => {
    setSelectedUser(user);
    setMatchLoading(true);

    try {
      // Get user's preferences
      const userPrefs = user.preferences;
      
      // Find potential matches based on preferences
      const matches = users
        .filter(u => u.id !== user.id)
        .map(u => {
          const score = calculateCompatibilityScore(userPrefs, u.preferences);
          return { ...u, compatibility_score: score };
        })
        .sort((a, b) => (b.compatibility_score || 0) - (a.compatibility_score || 0));

      setPotentialMatches(matches);
    } catch (error) {
      console.error('Error finding matches:', error);
    } finally {
      setMatchLoading(false);
    }
  };

  const calculateCompatibilityScore = (prefs1: any, prefs2: any): number => {
    let score = 0;
    const weights = {
      budget: 0.2,
      location: 0.2,
      lifestyle: 0.3,
      cleanliness: 0.15,
      schedule: 0.15
    };

    // Budget compatibility
    if (prefs1?.budget && prefs2?.budget) {
      const budgetOverlap = Math.min(prefs1.budget.max, prefs2.budget.max) - 
                           Math.max(prefs1.budget.min, prefs2.budget.min);
      if (budgetOverlap > 0) {
        score += weights.budget * (budgetOverlap / Math.min(prefs1.budget.max, prefs2.budget.max));
      }
    }

    // Location compatibility
    if (prefs1?.location?.neighborhoods && prefs2?.location?.neighborhoods) {
      const commonNeighborhoods = prefs1.location.neighborhoods
        .filter((n: string) => prefs2.location.neighborhoods.includes(n));
      score += weights.location * (commonNeighborhoods.length / 
        Math.max(prefs1.location.neighborhoods.length, prefs2.location.neighborhoods.length));
    }

    // Lifestyle compatibility
    if (prefs1?.lifestyle && prefs2?.lifestyle) {
      // Smoking
      if (prefs1.lifestyle.smoking === prefs2.lifestyle.smoking) {
        score += weights.lifestyle * 0.2;
      }
      
      // Pets
      if (prefs1.lifestyle.pets?.allowed === prefs2.lifestyle.pets?.allowed) {
        score += weights.lifestyle * 0.2;
      }
      
      // Guests
      if (prefs1.lifestyle.guests?.frequency === prefs2.lifestyle.guests?.frequency) {
        score += weights.lifestyle * 0.2;
      }
    }

    // Cleanliness compatibility
    if (prefs1?.lifestyle?.cleanliness && prefs2?.lifestyle?.cleanliness) {
      const cleanlinessMatch = Math.abs(prefs1.lifestyle.cleanliness - prefs2.lifestyle.cleanliness) <= 1;
      if (cleanlinessMatch) {
        score += weights.cleanliness;
      }
    }

    // Schedule compatibility
    if (prefs1?.lifestyle?.schedule && prefs2?.lifestyle?.schedule) {
      const scheduleMatch = prefs1.lifestyle.schedule.workFromHome === prefs2.lifestyle.schedule.workFromHome;
      if (scheduleMatch) {
        score += weights.schedule;
      }
    }

    return Math.round(score * 100);
  };

  const createMatch = async (userId1: string, userId2: string) => {
    try {
      // Create a match record
      const { error } = await supabase
        .from('matches')
        .insert({
          user1_id: userId1,
          user2_id: userId2,
          status: 'pending',
          created_by: 'admin',
          compatibility_score: potentialMatches.find(u => u.id === userId2)?.compatibility_score || 0
        });

      if (error) throw error;

      // Refresh matches
      await findPotentialMatches(selectedUser!);
    } catch (error) {
      console.error('Error creating match:', error);
    }
  };

  const renderProfileModal = () => {
    if (!selectedMatch) return null;

    const prefs = selectedMatch.preferences;
    const trustScore = selectedMatch.trust_score_data;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">{selectedMatch.display_name}</h2>
              <button
                onClick={() => {
                  setSelectedMatch(null);
                  setShowProfileModal(false);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Trust Score */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-2">Trust Score</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-5 h-5 text-yellow-500" />
                  <span className="font-medium">{trustScore.score} / 100</span>
                  <span className="text-sm text-gray-600">({trustScore.tier.replace('_', ' ')})</span>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  {Object.entries(trustScore.components).map(([key, value]) => (
                    <div key={key} className="text-sm">
                      <span className="text-gray-600 capitalize">{key.replace('_', ' ')}:</span>
                      <span className="ml-1 font-medium">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Preferences */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-2">Preferences</h3>
              <div className="space-y-4">
                {/* Budget */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Budget</h4>
                  <p>${prefs.budget?.min} - ${prefs.budget?.max} / month</p>
                </div>

                {/* Location */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Preferred Neighborhoods</h4>
                  <div className="flex flex-wrap gap-2">
                    {prefs.location?.neighborhoods?.map((n: string) => (
                      <span key={n} className="px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-sm">
                        {n}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Lifestyle */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Lifestyle</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-gray-600">Cleanliness:</span>
                      <span className="ml-1">{prefs.lifestyle?.cleanliness}/5</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Smoking:</span>
                      <span className="ml-1">{prefs.lifestyle?.smoking ? 'Yes' : 'No'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Pets:</span>
                      <span className="ml-1">{prefs.lifestyle?.pets?.allowed ? 'Allowed' : 'Not Allowed'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Guest Frequency:</span>
                      <span className="ml-1">{prefs.lifestyle?.guests?.frequency}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Dealbreakers */}
            {prefs.dealbreakers?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">Dealbreakers</h3>
                <div className="space-y-2">
                  {prefs.dealbreakers.map((d: any, i: number) => (
                    <div key={i} className="flex items-start gap-2 bg-red-50 p-3 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-700">{d.type}</p>
                        <p className="text-sm text-red-600">{d.description}</p>
                        <p className="text-xs text-red-500 mt-1">
                          Severity: {d.severity} (Impact: {d.impact}%)
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Vibe Tags */}
            {selectedMatch.vibe_tags?.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">Vibe Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedMatch.vibe_tags.map((tag, i) => (
                    <span key={i} className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Member Since */}
            <div className="text-sm text-gray-600">
              Member since {format(new Date(selectedMatch.created_at), 'MMMM d, yyyy')}
            </div>
          </div>

          <div className="p-6 bg-gray-50 flex justify-end gap-4">
            <button
              onClick={() => {
                setSelectedMatch(null);
                setShowProfileModal(false);
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Close
            </button>
            <button
              onClick={() => createMatch(selectedUser!.id, selectedMatch.id)}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center gap-2"
            >
              <UserPlus className="w-5 h-5" />
              Create Match
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* User List */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4">Select User to Match</h2>
          <div className="space-y-4">
            {users.map(user => (
              <button
                key={user.id}
                onClick={() => findPotentialMatches(user)}
                className={`w-full text-left p-4 rounded-lg border transition-colors ${
                  selectedUser?.id === user.id
                    ? 'border-primary-400 bg-primary-50'
                    : 'border-gray-200 hover:border-primary-200'
                }`}
              >
                <h3 className="font-medium">{user.display_name}</h3>
                <p className="text-sm text-gray-600 line-clamp-2">{user.bio}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Potential Matches */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4">Potential Matches</h2>
          {selectedUser ? (
            matchLoading ? (
              <div className="flex items-center justify-center p-8">
                <RefreshCw className="w-6 h-6 animate-spin text-primary-600" />
              </div>
            ) : (
              <div className="space-y-4">
                {potentialMatches.map(match => (
                  <div
                    key={match.id}
                    className="p-4 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium">{match.display_name}</h3>
                        <p className="text-sm text-gray-600 line-clamp-2">{match.bio}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-primary-600">
                          {match.compatibility_score}% Match
                        </span>
                        <button
                          onClick={() => {
                            setSelectedMatch(match);
                            setShowProfileModal(true);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-full"
                          title="View Profile"
                        >
                          <Filter className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => createMatch(selectedUser.id, match.id)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-full"
                          title="Create Match"
                        >
                          <UserPlus className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <p className="text-center text-gray-600 py-8">
              Select a user to see potential matches
            </p>
          )}
        </div>
      </div>

      {showProfileModal && renderProfileModal()}
    </>
  );
}